import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSocket, SOCKET_STATE } from './SocketContext';
import { useWebRTC } from './WebRTCContext';

/**
 * PeopleContext — the person layer of the "person → devices" model (Phase 1).
 *
 * The operator works on a PERSON (subject); a person owns one or more paired
 * devices. This context groups the flat device slots exposed by SocketContext
 * into people and drives selection at the person level — but it never replaces
 * SocketContext's targeting. Every selection here ultimately calls the existing
 * `selectSocket(index)`, so the single `selectedSocketIndex` remains the one
 * source of truth every feature already targets. This keeps all current
 * single-device behavior intact; the person model is purely additive.
 *
 * Grouping is operator-owned and persisted per monitor in localStorage. Devices
 * not yet assigned to any explicit person surface as their own single-device
 * "implicit" person, so every paired device is always reachable — the pre-Phase-1
 * behavior (one gem per device) is just the all-implicit case.
 *
 * Pair-time assignment: pairing a device "to a person" remembers that person and
 * attaches the next newly-bound session to them, so devices land in the right
 * group without a manual step.
 */

const PeopleContext = createContext(null);

export const usePeople = () => {
  const ctx = useContext(PeopleContext);
  if (!ctx) throw new Error('usePeople must be used within a PeopleProvider');
  return ctx;
};

const STORAGE_PREFIX = 'inquisitor_people:';
const storageKey = (monitorId) => `${STORAGE_PREFIX}${monitorId || 'default'}`;

const loadPeople = (monitorId) => {
  try {
    const raw = localStorage.getItem(storageKey(monitorId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed records.
    return parsed
      .filter(p => p && typeof p.id === 'string' && typeof p.name === 'string')
      .map(p => ({ id: p.id, name: p.name, sessionIds: Array.isArray(p.sessionIds) ? p.sessionIds : [] }));
  } catch {
    return [];
  }
};

let _personSeq = 0;
const newPersonId = () => `person_${_personSeq++}_${Math.round(performance.now())}`;

export const PeopleProvider = ({ children }) => {
  const { sockets, selectedSocket, selectSocket, generateCodeForSlot, cancelPairingCode } = useSocket();
  const { monitorId } = useWebRTC();

  // Operator-defined groups. Implicit single-device people are derived, not stored.
  const [people, setPeople] = useState(() => loadPeople(monitorId));

  // The person the next newly-bound session should join (pair-time assignment).
  const [pendingBindPersonId, setPendingBindPersonId] = useState(null);
  // The slot currently showing a code we generated for a person (for the UI).
  const [pendingPairing, setPendingPairing] = useState(null); // { personId, slotIndex } | null

  // Persist groups whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(monitorId), JSON.stringify(people));
    } catch { /* storage full / unavailable — non-fatal */ }
  }, [people, monitorId]);

  // Bound device slots (online + offline), each carrying its slot index.
  const boundDevices = useMemo(
    () => sockets.filter(s => s.sessionId && (s.state === SOCKET_STATE.BOUND_ONLINE || s.state === SOCKET_STATE.BOUND_OFFLINE)),
    [sockets]
  );

  const assignSessionToPerson = useCallback((sessionId, personId) => {
    if (!sessionId || !personId) return;
    setPeople(prev => prev.map(p => ({
      ...p,
      sessionIds: p.id === personId
        ? Array.from(new Set([...p.sessionIds, sessionId]))
        : p.sessionIds.filter(id => id !== sessionId),
    })));
  }, []);

  // Attach a newly-bound session to the person we were pairing for.
  const prevBoundIdsRef = useRef(null);
  useEffect(() => {
    const currentIds = boundDevices.map(d => d.sessionId);
    const prev = prevBoundIdsRef.current;
    prevBoundIdsRef.current = currentIds;
    if (prev === null) return; // first observation — establish baseline only

    const fresh = currentIds.filter(id => !prev.includes(id));
    if (fresh.length && pendingBindPersonId) {
      // One device is paired at a time; attach the first new session and clear.
      assignSessionToPerson(fresh[0], pendingBindPersonId);
      setPendingBindPersonId(null);
      setPendingPairing(null);
    }
  }, [boundDevices, pendingBindPersonId, assignSessionToPerson]);

  // Clear a stale pending-pairing marker once its slot is no longer pending
  // (the code was claimed, cancelled, or expired). The marker is set the instant
  // the operator clicks pair, but the code only becomes live after an async
  // WebSocket round-trip — so the slot is briefly still EMPTY. We must NOT clear
  // during that in-flight window (doing so nuked the marker before the code
  // arrived, so the pairing pill never showed). Only clear once the code has
  // actually gone live and then disappeared, with a safety timeout in case the
  // request never returns.
  useEffect(() => {
    if (!pendingPairing) return undefined;
    const slot = sockets[pendingPairing.slotIndex];

    if (slot && slot.state === SOCKET_STATE.PENDING) {
      if (!pendingPairing.live) setPendingPairing(pp => (pp ? { ...pp, live: true } : pp));
      return undefined;
    }

    // Slot isn't pending. If the code was live (now claimed/cancelled/expired) or
    // the slot moved to some non-empty state (e.g. bound), drop the marker now.
    if (pendingPairing.live || (slot && slot.state !== SOCKET_STATE.EMPTY)) {
      setPendingPairing(null);
      return undefined;
    }

    // Still waiting for the initial code to arrive — give it a grace period
    // rather than clearing immediately.
    const t = setTimeout(() => setPendingPairing(null), 15000);
    return () => clearTimeout(t);
  }, [sockets, pendingPairing]);

  // Build the display roster: explicit people (always shown, even with no bound
  // devices yet) followed by an implicit person per unassigned bound device.
  const roster = useMemo(() => {
    const assigned = new Set(people.flatMap(p => p.sessionIds));
    const deviceFor = (device) => ({
      sessionId: device.sessionId,
      name: device.sessionName || device.shortName || device.sessionId?.slice(0, 8),
      slotIndex: device.index,
      online: device.state === SOCKET_STATE.BOUND_ONLINE,
      isActive: selectedSocket?.sessionId === device.sessionId,
    });

    const explicit = people.map(p => {
      const devices = boundDevices
        .filter(d => p.sessionIds.includes(d.sessionId))
        .map(deviceFor);
      return {
        id: p.id,
        name: p.name,
        isImplicit: false,
        devices,
        deviceCount: devices.length,
        onlineCount: devices.filter(d => d.online).length,
      };
    });

    const implicit = boundDevices
      .filter(d => !assigned.has(d.sessionId))
      .map(d => {
        const device = deviceFor(d);
        return {
          id: `auto:${d.sessionId}`,
          name: device.name,
          isImplicit: true,
          devices: [device],
          deviceCount: 1,
          onlineCount: device.online ? 1 : 0,
        };
      });

    return [...explicit, ...implicit].sort((a, b) => {
      // People with someone online float up; then by name.
      if ((b.onlineCount > 0) !== (a.onlineCount > 0)) return b.onlineCount - a.onlineCount;
      return a.name.localeCompare(b.name);
    });
  }, [people, boundDevices, selectedSocket]);

  // Which person owns the currently-selected device.
  const selectedPersonId = useMemo(() => {
    if (!selectedSocket?.sessionId) return null;
    const found = roster.find(p => p.devices.some(d => d.sessionId === selectedSocket.sessionId));
    return found?.id || null;
  }, [roster, selectedSocket]);

  const selectedPerson = useMemo(
    () => roster.find(p => p.id === selectedPersonId) || null,
    [roster, selectedPersonId]
  );

  // --- Actions ---

  // Select a person: target their active device (prefer an online one).
  const selectPerson = useCallback((personId) => {
    const person = roster.find(p => p.id === personId);
    if (!person || person.devices.length === 0) return;
    const target = person.devices.find(d => d.online) || person.devices[0];
    selectSocket(target.slotIndex);
  }, [roster, selectSocket]);

  // Switch the active device within the current person.
  const setActiveDevice = useCallback((sessionId) => {
    const device = boundDevices.find(d => d.sessionId === sessionId);
    if (device) selectSocket(device.index);
  }, [boundDevices, selectSocket]);

  const createPerson = useCallback((name) => {
    const id = newPersonId();
    setPeople(prev => [...prev, { id, name: (name || 'New person').trim() || 'New person', sessionIds: [] }]);
    return id;
  }, []);

  const renamePerson = useCallback((personId, name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    setPeople(prev => prev.map(p => (p.id === personId ? { ...p, name: clean } : p)));
  }, []);

  // Remove an explicit person; its devices fall back to implicit (still reachable).
  const removePerson = useCallback((personId) => {
    setPeople(prev => prev.filter(p => p.id !== personId));
  }, []);

  // Detach a device from its person (becomes its own implicit person).
  const ungroupDevice = useCallback((sessionId) => {
    setPeople(prev => prev.map(p => ({ ...p, sessionIds: p.sessionIds.filter(id => id !== sessionId) })));
  }, []);

  // Pair a new device and attach it to `personId` once it binds. If personId is
  // an implicit id, it's promoted to an explicit person first so the pairing has
  // somewhere durable to land. Returns the slot index used, or null if full.
  const pairDeviceToPerson = useCallback((personId, role = 'full') => {
    const emptySlot = sockets.find(s => s.state === SOCKET_STATE.EMPTY);
    if (!emptySlot) return null;

    let targetId = personId;
    if (personId && personId.startsWith('auto:')) {
      // Promote the implicit person: create an explicit group seeded with its device.
      const sessionId = personId.slice('auto:'.length);
      const device = boundDevices.find(d => d.sessionId === sessionId);
      const id = newPersonId();
      setPeople(prev => [...prev, { id, name: device?.sessionName || 'New person', sessionIds: sessionId ? [sessionId] : [] }]);
      targetId = id;
    }

    setPendingBindPersonId(targetId || null);
    setPendingPairing(targetId ? { personId: targetId, slotIndex: emptySlot.index } : null);
    generateCodeForSlot(emptySlot.index, role);
    return emptySlot.index;
  }, [sockets, boundDevices, generateCodeForSlot]);

  // Create a brand-new person and immediately start pairing their first device.
  const createPersonAndPair = useCallback((name, role = 'full') => {
    const id = createPerson(name);
    const emptySlot = sockets.find(s => s.state === SOCKET_STATE.EMPTY);
    if (emptySlot) {
      setPendingBindPersonId(id);
      setPendingPairing({ personId: id, slotIndex: emptySlot.index });
      generateCodeForSlot(emptySlot.index, role);
    }
    return id;
  }, [createPerson, sockets, generateCodeForSlot]);

  const cancelPairing = useCallback(() => {
    if (pendingPairing) cancelPairingCode(pendingPairing.slotIndex);
    setPendingBindPersonId(null);
    setPendingPairing(null);
  }, [pendingPairing, cancelPairingCode]);

  // The live pending pairing code (from the SocketContext slot), for the UI.
  const pendingCode = useMemo(() => {
    if (!pendingPairing) return null;
    const slot = sockets[pendingPairing.slotIndex];
    if (!slot || slot.state !== SOCKET_STATE.PENDING) return null;
    return { personId: pendingPairing.personId, code: slot.pairingCode, expiresIn: slot.codeExpiresIn };
  }, [pendingPairing, sockets]);

  const value = {
    roster,
    selectedPerson,
    selectedPersonId,
    hasPeople: roster.length > 0,
    pendingCode,
    // Actions
    selectPerson,
    setActiveDevice,
    createPerson,
    renamePerson,
    removePerson,
    assignSessionToPerson,
    ungroupDevice,
    pairDeviceToPerson,
    createPersonAndPair,
    cancelPairing,
  };

  return <PeopleContext.Provider value={value}>{children}</PeopleContext.Provider>;
};

export default PeopleContext;
